import { useCallback, useEffect, useState } from 'react';
import {
  Page,
  Card,
  BlockStack,
  Text,
  Badge,
  Button,
  Banner,
  InlineStack,
  Divider,
  Modal,
  Box,
  ProgressBar,
  Select,
  Checkbox,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { PLANS, PlanKey, nextPlanUp, planInfo } from '../plans';

interface BillingStatus {
  status: string;
  subscriptionId: string | null;
  trialEndsAt: string | null;
  planKey?: PlanKey;
  planName: string;
  price: string;
  usage?: { used: number; limit: number; remaining: number; resetsAt: string };
}

function statusBadge(status: string) {
  switch (status) {
    case 'ACTIVE':   return <Badge tone="success">Active</Badge>;
    case 'PENDING':  return <Badge tone="attention">Pending</Badge>;
    case 'CANCELLED':return <Badge tone="critical">Cancelled</Badge>;
    case 'FROZEN':   return <Badge tone="warning">Frozen</Badge>;
    case 'EXPIRED':  return <Badge tone="critical">Expired</Badge>;
    case 'DECLINED': return <Badge tone="critical">Declined</Badge>;
    default:         return <Badge>No subscription</Badge>;
  }
}

interface SigningSettings {
  reminderEnabled: boolean;
  linkExpiryDays: number;
  linkDayOptions: number[];
}

/** Reminder emails on/off and how long new signing links stay valid. */
function SigningSettingsCard() {
  const { request } = useApi();
  const [settings, setSettings] = useState<SigningSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    request<SigningSettings>('/merchant/settings').then(setSettings).catch(() => setError('Could not load these settings.'));
  }, [request]);

  const save = useCallback(async (patch: Partial<SigningSettings>) => {
    setSaving(true);
    setError(null);
    try {
      const next = await request<SigningSettings>('/merchant/settings', { method: 'PUT', body: JSON.stringify(patch) });
      setSettings(next);
      (window as any).shopify?.toast?.show('Settings saved');
    } catch (e: any) {
      setError(e.message ?? 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [request]);

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h2">Signing links &amp; reminders</Text>
        <Divider />
        {error && <Banner tone="critical" onDismiss={() => setError(null)}><p>{error}</p></Banner>}
        {!settings ? (
          <Text as="p" tone="subdued">Loading…</Text>
        ) : (
          <BlockStack gap="400">
            <Box maxWidth="320px">
              <Select
                label="Signing links stay valid for"
                options={settings.linkDayOptions.map((d) => ({ label: `${d} days`, value: String(d) }))}
                value={String(settings.linkExpiryDays)}
                onChange={(v) => save({ linkExpiryDays: Number(v) })}
                disabled={saving}
                helpText="Applies to new signing requests. Links already sent keep their date."
              />
            </Box>
            <Checkbox
              label="Send reminder emails"
              checked={settings.reminderEnabled}
              onChange={(v) => save({ reminderEnabled: v })}
              disabled={saving}
              helpText="Customers who haven't signed yet get up to 2 friendly reminders — about 1 day and 3 days after the request. Reminders are free and don't count towards your signing requests. (Documents with one signer.)"
            />
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

export default function SettingsPage() {
  const { request, startSubscription } = useApi();
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await request<BillingStatus>('/auth/billing-status');
      setBilling(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleCancel = useCallback(async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      await request('/auth/billing-cancel', { method: 'POST' });
      setCancelSuccess(true);
      setCancelOpen(false);
      await fetchStatus();
    } catch (e: any) {
      setCancelError(e.message ?? 'Cancellation failed. Please try again.');
    } finally {
      setCancelling(false);
    }
  }, [request, fetchStatus]);

  const [subscribing, setSubscribing] = useState(false);

  // Subscribe (no plan → server keeps the store's current plan) or change plan.
  const handleSubscribe = useCallback(async (plan?: PlanKey) => {
    setSubscribing(true);
    setCancelError(null);
    try {
      await startSubscription(plan);
    } catch (e: any) {
      setCancelError(e.message ?? 'Could not start subscription.');
      setSubscribing(false);
    }
  }, [startSubscription]);

  const isActive = billing?.status === 'ACTIVE';

  return (
    <Page title="Settings">
      <BlockStack gap="500">

        {cancelSuccess && (
          <Banner
            title="Subscription cancelled"
            tone="info"
            onDismiss={() => setCancelSuccess(false)}
          >
            <p>Your {billing?.planName ?? 'SignShield'} subscription has been cancelled. You can resubscribe at any time below.</p>
          </Banner>
        )}

        {/* Plan & Billing */}
        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Plan &amp; Billing</Text>
            <Divider />

            {loading ? (
              <Text as="p" tone="subdued">Loading subscription details…</Text>
            ) : billing ? (
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="semibold">{billing.planName}</Text>
                    <Text as="p" tone="subdued">{billing.price}</Text>
                  </BlockStack>
                  {statusBadge(billing.status)}
                </InlineStack>

                {billing.trialEndsAt && billing.status === 'ACTIVE' &&
                  new Date(billing.trialEndsAt) > new Date() && (
                  <Text as="p" tone="subdued">
                    Free trial ends {new Date(billing.trialEndsAt).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric',
                    })}
                  </Text>
                )}

                {billing.usage && (
                  <BlockStack gap="200">
                    <InlineStack align="space-between">
                      <Text as="p">Signing requests this month</Text>
                      <Text as="p" fontWeight="semibold">
                        {billing.usage.used} of {billing.usage.limit}
                      </Text>
                    </InlineStack>
                    <ProgressBar
                      progress={Math.min(100, (billing.usage.used / billing.usage.limit) * 100)}
                      tone={billing.usage.remaining === 0 ? 'critical' : 'primary'}
                      size="small"
                    />
                    <Text as="p" variant="bodySm" tone="subdued">
                      {billing.usage.remaining === 0
                        ? 'Limit reached — new signing requests are paused until '
                        : 'Resets on '}
                      {new Date(billing.usage.resetsAt).toLocaleDateString('en-US', {
                        month: 'long', day: 'numeric', timeZone: 'UTC',
                      })}
                      . Re-sending an existing request doesn't count.
                    </Text>
                  </BlockStack>
                )}

                {cancelError && (
                  <Banner tone="critical" title="Cancellation failed">
                    <p>{cancelError}</p>
                  </Banner>
                )}

                {isActive && (() => {
                  const current = planInfo(billing.planKey);
                  const next = nextPlanUp(current.key);
                  // Upgrades first (smallest first), then smaller plans.
                  const others = [
                    ...PLANS.filter((p) => p.monthlyLimit > current.monthlyLimit),
                    ...PLANS.filter((p) => p.monthlyLimit < current.monthlyLimit).reverse(),
                  ];
                  return others.map((other) => {
                    const upgrade = other.monthlyLimit > current.monthlyLimit;
                    // Losing multiple signers: templates with several signers stop sending.
                    const losesSigners = current.multiSigner && !other.multiSigner;
                    return (
                      <Box key={other.key} padding="300" borderWidth="025" borderColor="border" borderRadius="200">
                        <InlineStack align="space-between" blockAlign="center" gap="300">
                          <BlockStack gap="050">
                            <Text as="p" fontWeight="semibold">
                              {other.key === next?.key ? `Need more? Upgrade to ${other.name}` : upgrade ? `Upgrade to ${other.name}` : `Switch to ${other.name}`}
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                              {other.monthlyLimit} signing requests per month for ${other.price}/month
                              {other.multiSigner ? ', multiple signers included' : ''}.
                              {losesSigners ? ' Templates with several signers can no longer be sent on this plan.' : ''}
                              {' '}Shopify asks you to approve the new price; it replaces your current plan as soon as you approve.
                            </Text>
                          </BlockStack>
                          <Button
                            variant={other.key === next?.key ? 'primary' : 'secondary'}
                            onClick={() => handleSubscribe(other.key)}
                            loading={subscribing}
                          >
                            {upgrade ? `Upgrade — $${other.price}/month` : `Switch to ${other.name}`}
                          </Button>
                        </InlineStack>
                      </Box>
                    );
                  });
                })()}

                {isActive && (
                  <Box paddingBlockStart="200">
                    <Button
                      tone="critical"
                      variant="plain"
                      onClick={() => setCancelOpen(true)}
                    >
                      Cancel subscription
                    </Button>
                  </Box>
                )}

                {!isActive && (
                  <Box paddingBlockStart="200">
                    <Button variant="primary" onClick={() => handleSubscribe()} loading={subscribing}>
                      {`Subscribe to ${billing.planName}`}
                    </Button>
                  </Box>
                )}
              </BlockStack>
            ) : (
              <Text as="p" tone="subdued">No subscription found.</Text>
            )}
          </BlockStack>
        </Card>

        <SigningSettingsCard />

        {/* Support */}
        <Card>
          <BlockStack gap="300">
            <Text variant="headingMd" as="h2">Support</Text>
            <Divider />
            <Text as="p" tone="subdued">
              Need help? Email us at{' '}
              <a href="mailto:support@merchantkits.com" style={{ color: 'inherit' }}>
                support@merchantkits.com
              </a>
            </Text>
          </BlockStack>
        </Card>

      </BlockStack>

      {/* Cancel confirmation modal */}
      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancel subscription?"
        primaryAction={{
          content: 'Cancel subscription',
          destructive: true,
          loading: cancelling,
          onAction: handleCancel,
        }}
        secondaryActions={[{
          content: 'Keep subscription',
          onAction: () => setCancelOpen(false),
        }]}
      >
        <Modal.Section>
          <Text as="p">
            Cancelling ends your {billing?.planName ?? 'SignShield'} subscription immediately. New signing requests will
            stop being sent. Documents already signed remain verifiable through their verification
            links, and you can resubscribe at any time from this page.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
